import React from 'react';
import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';
import { getFileUrl } from '@/services/CommonService';

const fmtDate = (v) => {
  if (!v) return '—';
  const d = typeof v === 'string' || typeof v === 'number' ? new Date(v) : v;
  return isNaN(d?.getTime?.()) ? '—' : d.toLocaleString();
};
const safeNum = (n, fallback = 0) => {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
};
const orderTotal = (o) =>
  safeNum(
    o?.total ??
      o?.grand_total ??
      o?.grandTotal ??
      o?.amount ??
      o?.net_total ??
      o?.netTotal,
    0
  );

const statusBadge = (label, tone = 'gray') => {
  const tones = {
    green: 'bg-green-50 text-green-700 ring-green-600/20',
    red: 'bg-red-50 text-red-700 ring-red-600/20',
    amber: 'bg-amber-50 text-amber-700 ring-amber-600/20',
    blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    gray: 'bg-gray-100 text-gray-700 ring-gray-600/20',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${tones[tone] || tones.gray}`}>
      {label || '—'}
    </span>
  );
};

const ItemChips = ({ items = [] }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <span className="text-xs text-gray-500">No items</span>;
  }
  const first = items.slice(0, 3);
  const more  = Math.max(0, items.length - first.length);

  return (
    <div className="flex flex-wrap gap-2">
      {first.map((it, idx) => (
        <div key={idx} className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1">
          <div className="w-6 h-6 rounded-md overflow-hidden bg-white border border-gray-100">
            {it?.file_name ? (
              <img
                src={getFileUrl(it.file_name, 'product')}
                alt={it?.name || 'item'}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full grid place-items-center text-[10px] text-gray-400">IMG</div>
            )}
          </div>
          <span className="text-xs text-gray-700">
            {it?.name || 'Item'}{it?.quantity ? ` × ${it.quantity}` : ''}
          </span>
        </div>
      ))}
      {more > 0 && (
        <span className="text-xs text-gray-500">+{more} more</span>
      )}
    </div>
  );
};

const OrderCard = ({ o, onView }) => {
  const id        = o?._id || '';
  const orderNo   = o?.order_no || o?.orderNo || id.slice(-7);
  const createdAt = o?.created_at || o?.createdAt || o?.date || o?.created_at_utc;
  const items     = Array.isArray(o?.items) ? o.items : Array.isArray(o?.cartItems) ? o.cartItems : [];
  const total     = orderTotal(o);

  const payStatus   = (o?.payment_status || o?.paymentStatus || '').toString().toLowerCase();
  const orderStatus = (o?.order_status   || o?.orderStatus   || '').toString().toLowerCase();
  const typeLabel   = o?.order_type_name || o?.orderTypeLabel || (o?.order_type === 2 ? 'Dine-in' : o?.order_type === 3 ? 'Delivery' : 'Order');

  const payTone =
    payStatus.includes('paid') ? 'green'
    : payStatus.includes('failed') ? 'red'
    : payStatus.includes('pending') ? 'amber'
    : 'gray';

  const ordTone =
    orderStatus.includes('completed') || orderStatus.includes('delivered') ? 'green'
    : orderStatus.includes('cancel')  ? 'red'
    : orderStatus.includes('ready') || orderStatus.includes('preparing') ? 'blue'
    : 'gray';

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition">
      {/* Accent bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary/80 to-primary/40" />

      <div className="p-4 sm:p-5 ltr:pl-5 rtl:pr-5 ltr:sm:pl-6 rtl:sm:pr-6">
        {/* Header row */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-heading">Order #{orderNo}</h3>
              {statusBadge(typeLabel, 'blue')}
            </div>
            <span className="text-xs text-gray-500">{fmtDate(createdAt)}</span>
          </div>

          <div className="flex items-center gap-2">
            {statusBadge((o?.payment_status || o?.paymentStatus || 'Payment').toString(), payTone)}
            {statusBadge((o?.order_status   || o?.orderStatus   || 'Status').toString(), ordTone)}
          </div>
        </div>

        {/* Items preview */}
        <div className="mt-4">
          <ItemChips items={items} />
        </div>

        {/* Footer row */}
        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-gray-600">
            <span className="mr-2">Payment:</span>
            <strong className="text-heading">{o?.payment_method?.name || o?.paymentMethod?.name || o?.payment_method_name || '—'}</strong>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="block text-xs text-gray-500">Total</span>
              <div className="text-lg font-semibold text-heading">
                <CurrencyCodeAdd price={total} />
              </div>
            </div>

            <button
              type="button"
              onClick={() => onView?.(id)}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              <i className="lab lab-eye" />
              View details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SkeletonCard = () => (
  <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
    <div className="p-5 animate-pulse space-y-4">
      <div className="h-5 w-40 bg-gray-200 rounded" />
      <div className="h-3 w-24 bg-gray-200 rounded" />
      <div className="h-8 w-full bg-gray-200 rounded" />
      <div className="flex items-center justify-between">
        <div className="h-4 w-28 bg-gray-200 rounded" />
        <div className="h-6 w-24 bg-gray-200 rounded" />
      </div>
    </div>
  </div>
);

const EmptyState = () => (
  <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center">
    <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary">
      <i className="lab lab-bag-2" />
    </div>
    <h4 className="text-base font-semibold text-heading">No orders yet</h4>
    <p className="mt-1 text-sm text-gray-500">When you place an order, it will show up here.</p>
  </div>
);

const RecentOrdersList = ({ orders = [], isLoading = false, onView }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }
  if (!orders.length) return <EmptyState />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {orders.map((o) => (
        <OrderCard key={o?._id || Math.random()} o={o} onView={onView} />
      ))}
    </div>
  );
};

export default RecentOrdersList;
